import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { coursesApi } from '@/api/courses';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';

const LANGUAGE_KEY: Record<string, string> = {
  EN: 'course.langEn',
  RU: 'course.langRu',
  KZ: 'course.langKz',
};

function languageLabelKey(lang?: string): string {
  if (!lang) return 'course.langEn';
  return LANGUAGE_KEY[lang.toUpperCase()] ?? lang;
}

/**
 * Публичная страница курса (ТЗ §11.4): только программа.
 *
 * Тарифов, цен и кнопки «Выбрать тариф» здесь нет и быть не должно.
 * Страница открыта без входа и лежит в одном клике от главной, поэтому
 * прайс-лист на ней читается как витрина платного доступа к тому же
 * содержимому, что открывает мобильное приложение. Именно на этот след
 * (сайт → цена → «Выбрать») ссылалась проверка App Store 30.08.2026.
 *
 * Кнопки формы контактов здесь тоже нет (решение владельца 15.09.2026, подача
 * по Apple 3.1.3(f)). Форма контактов требует телефон и контакты родителя, а
 * сервер /leads без них заявку не принимает, так что убрать одни поля нельзя —
 * получилась бы форма, которая не отправляется. Форма и ручка /leads остались
 * в коде нетронутыми; после подачи сюда вернётся форма «имя + почта» отдельной
 * задачей.
 */
export function CoursePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const marathon = useQuery({
    queryKey: ['public', 'marathon', slug],
    queryFn: () => coursesApi.getMarathonBySlug(slug!),
    enabled: !!slug,
  });

  if (marathon.isLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
        <p className="text-on-surface-variant">{t('course.loading')}</p>
      </div>
    );
  }
  if (!marathon.data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-lg text-on-surface-variant">
          {t('course.notFound')}
        </p>
        <Link to="/">
          <Button>{t('course.backToCatalog')}</Button>
        </Link>
      </div>
    );
  }
  const m = marathon.data;

  return (
    <div className="min-h-screen bg-background">
      <header className="px-6 py-6 md:px-10 md:py-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/"><span className="font-display text-2xl font-extrabold tracking-display text-primary">{IS_APP_BUILD ? APP_BRAND : 'Onebilim'}</span></Link>
          <Link to="/register"><Button size="sm">{t('auth.signUp')}</Button></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-6xl">
          {m.level_start && m.level_end && (
            <Pill variant="primary">{m.level_start} → {m.level_end}</Pill>
          )}
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-display md:text-5xl">{m.title}</h1>
          <p className="mt-4 max-w-2xl text-on-surface-variant">
            {(m.category?.trim() || t('landing.defaultCourseCategory'))} · {t(languageLabelKey(m.language))}
          </p>
        </div>
      </section>

      {/* Program */}
      <section className="bg-surface-container-low px-6 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-6 font-display text-3xl font-bold tracking-display">{t('course.programTitle')}</h2>
          {m.modules?.length ? (
            <ol className="space-y-4">
              {m.modules.map((mod, idx) => (
                <li key={mod.id}>
                  <Card>
                    <CardTitle>{idx + 1}. {mod.title}</CardTitle>
                    {mod.lessons && mod.lessons.length > 0 && (
                      <ul className="mt-3 grid gap-1.5 md:grid-cols-2">
                        {mod.lessons.map((l) => (
                          <li key={l.id} className="flex items-center gap-2 text-sm text-on-surface-variant">
                            <span className="material-symbols-outlined text-base">menu_book</span>
                            {l.title}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-on-surface-variant">{t('course.programEmpty')}</p>
          )}
        </div>
      </section>

      <footer className="bg-inverse-surface px-6 py-8 text-inverse-on-surface">
        <div className="mx-auto max-w-6xl text-sm">© {new Date().getFullYear()} {IS_APP_BUILD ? APP_BRAND : 'Onebilim'}</div>
      </footer>
    </div>
  );
}
